import { useState } from 'react';
import { Button, InfoBox, Radio, StatusBadge, Textarea } from '@immo/ui-kit';
import { FLEET_QUESTION_ANSWER_MAX_CHARS, type FleetBoardRunQuestion } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';

/** The own-answer radio's sentinel value — never a real option key. */
const OWN_ANSWER = 'own';

type Props = {
  readonly question: FleetBoardRunQuestion;
  readonly ticket: string;
  /** Called once the question was answered, or turned out to be resolved already: the panel re-reads the ticket. */
  readonly onResolved: () => void;
};

function errorCodeOf(body: unknown): string | undefined {
  return (body as { error?: { code?: string } } | undefined)?.error?.code;
}

/**
 * Answers an open question from the board (IMM-981, OPS-79), through the path a messenger reply takes: the answer is
 * recorded once, and the question's Telegram or WhatsApp session is closed, its buttons taken off where they can be.
 */
export function QuestionAnswer({ question, ticket, onResolved }: Props) {
  const t = useTranslations('pages.admin.jobs');
  const tErrors = useTranslations('errors');
  const [choice, setChoice] = useState<string | null>(
    question.options.find((option) => option.recommended)?.key ?? (question.options.length === 0 ? OWN_ANSWER : null),
  );
  const [ownAnswer, setOwnAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [askedAgain, setAskedAgain] = useState(false);

  const failWith = (body: unknown) => {
    const code = errorCodeOf(body);
    setError(code !== undefined ? tErrors(code) : t('generic-error'));

    // Both codes mean somebody else already resolved the question: the panel drops the form.
    if (code === 'fleet.question.already-answered' || code === 'fleet.question.not-open') onResolved();
  };

  const submit = async () => {
    const option = question.options.find(({ key }) => key === choice) ?? null;
    const answer = option !== null ? `(${option.key}) ${option.text}` : ownAnswer.trim();

    if (answer.length === 0) return;

    setBusy(true);
    setError(null);

    try {
      const result = await apiClient.adminFleetQuestions.answer({
        pathParams: { questionId: question.id },
        body: { answer },
      });

      if (result.ok) onResolved();
      else failWith(result.body);
    } finally {
      setBusy(false);
    }
  };

  const askAgain = async () => {
    setBusy(true);
    setError(null);
    setAskedAgain(false);

    try {
      const result = await apiClient.adminFleetQuestions.askAgain({
        pathParams: { questionId: question.id },
        body: {},
      });

      if (result.ok) setAskedAgain(true);
      else failWith(result.body);
    } finally {
      setBusy(false);
    }
  };

  const name = `fleet-question-answer-${question.id}`;
  const disabled = busy || choice === null || (choice === OWN_ANSWER && ownAnswer.trim().length === 0);

  return (
    <div className="flex flex-col gap-3">
      {question.options.length > 0 && (
        <fieldset className="flex flex-col gap-2">
          <legend className="text-xs text-text-tertiary">{t('question-options-heading')}</legend>
          {question.options.map((option) => (
            <label key={option.key} className="flex items-start gap-2 text-sm text-text-primary">
              <Radio
                name={name}
                checked={choice === option.key}
                onSelect={() => setChoice(option.key)}
                className="mt-0.5"
              />
              <span>
                ({option.key}) {option.text}
                {option.recommended && (
                  <StatusBadge label={t('recommended-badge')} tone="neutral" size="sm" className="ml-2 align-middle" />
                )}
              </span>
            </label>
          ))}
          <label className="flex items-start gap-2 text-sm text-text-primary">
            <Radio
              name={name}
              checked={choice === OWN_ANSWER}
              onSelect={() => setChoice(OWN_ANSWER)}
              className="mt-0.5"
            />
            <span>{t('own-answer')}</span>
          </label>
        </fieldset>
      )}

      {choice === OWN_ANSWER && (
        <Textarea
          // Its own name, not the radio's: two controls both called „Eigene Antwort" cannot be told apart.
          aria-label={t('own-answer-input')}
          maxLength={FLEET_QUESTION_ANSWER_MAX_CHARS}
          value={ownAnswer}
          onChange={(event) => setOwnAnswer(event.target.value)}
          disabled={busy}
        />
      )}

      {error !== null && (
        <p role="alert" className="text-sm text-danger-600">
          {error}
        </p>
      )}

      {askedAgain && <InfoBox type="success">{t('ask-again-success', { ticket })}</InfoBox>}

      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" onClick={() => void submit()} disabled={disabled}>
          {t('answer-cta')}
        </Button>
        <Button type="button" size="sm" variant="secondary" onClick={() => void askAgain()} disabled={busy}>
          {t('ask-again-cta')}
        </Button>
      </div>
    </div>
  );
}
